import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { newAxiosClient } from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";
import {
  PageContainer,
  PageTopbar,
  PageHeader,
  ContentContainer,
  LoadableCardGrid,
  PreviewCard,
  BreadcrumbItem,
  astryx,
  neutralTheme,
  Plus,
  Search,
  useDocumentTheme,
  defaultGridPagination,
  type IPagination,
} from "@shadowcue/ui";

interface ScriptTemplatesProps {
  currentUser: IUser;
}

interface IScriptTemplate {
  name: string;
  id: number;
  description: string | null;
  createdAt: string;
  scriptTemplateSectionsCount: number;
  scriptTemplateQuestionsCount: number;
}

const axios = newAxiosClient();

const pluralize = (count: number, noun: string) =>
  `${count} ${count === 1 ? noun : `${noun}s`}`;

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "name", label: "Name A–Z" },
];

const DEBOUNCE_DURATION = 500;

const ScriptTemplates = (props: ScriptTemplatesProps) => {
  const [scriptTemplates, setScriptTemplates] = useState<IScriptTemplate[]>([]);
  const [pagination, setPagination] =
    useState<IPagination>(defaultGridPagination());
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [sort, setSort] = useState<string>("newest");
  const [dateRange, setDateRange] = useState<astryx.DateRange | null>(null);

  const navigate = useNavigate();
  const mode = useDocumentTheme();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Script Templates", href: "/admin/script_templates" },
  ];

  const fetchScriptTemplates = async (page: number = pagination.page) => {
    setLoading(true);
    try {
      const res = await axios.get("/api/script_templates", {
        headers: {
          AUTHORIZATION: `Bearer ${props.currentUser.authToken}`,
        },
        params: {
          includeCounts: true,
          page,
          limit: pagination.limit,
          search: debouncedSearch || undefined,
          sort,
          createdAfter: dateRange?.start,
          createdBefore: dateRange?.end,
        },
      });
      setScriptTemplates(res.data.scriptTemplates || []);
      setPagination(res.data.pagination || defaultGridPagination());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), DEBOUNCE_DURATION);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchScriptTemplates(1);
  }, [debouncedSearch, sort, dateRange]);

  const hasTemplates = !loading && scriptTemplates.length > 0;
  const isNarrowing = debouncedSearch.trim() !== "" || dateRange !== null;

  const clearFilters = () => {
    setSearch("");
    setSort("newest");
    setDateRange(null);
  };

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <PageContainer>
        <PageTopbar breadcrumbs={breadcrumbs} />

        <ContentContainer loading={false}>
          <PageHeader
            title="Script Templates"
            description="Create and manage your script templates."
            action={
              <astryx.Button
                label="New template"
                variant="primary"
                icon={<astryx.Icon icon={Plus} />}
                href="/admin/script_templates/new"
              />
            }
          />

          <astryx.VStack gap={6}>
            <astryx.HStack gap={3} align="center" wrap="wrap">
              <astryx.StackItem size="fill">
                <astryx.TextInput
                  label="Search templates"
                  isLabelHidden
                  startIcon={<astryx.Icon icon={Search} />}
                  placeholder="Search by name…"
                  hasClear
                  width="100%"
                  value={search}
                  onChange={(value: string) => setSearch(value)}
                />
              </astryx.StackItem>

              <astryx.Selector
                label="Sort templates"
                isLabelHidden
                value={sort}
                options={SORT_OPTIONS}
                onChange={(value: string) => setSort(value)}
              />

              <astryx.DateRangeInput
                label="Filter by date created"
                isLabelHidden
                placeholder="Date created"
                hasClear
                value={dateRange}
                onChange={(value: astryx.DateRange | null) => setDateRange(value)}
              />
            </astryx.HStack>

            <LoadableCardGrid
              loading={loading}
              emptyState={
                isNarrowing
                  ? {
                      headline: "No templates match",
                      subtext: "Try a different search or clear your filters.",
                      action: (
                        <astryx.Button
                          label="Clear filters"
                          onClick={clearFilters}
                        />
                      ),
                    }
                  : {
                      headline: "Create your first script template",
                      subtext:
                        "Script templates define the questions your AI-guided screens ask.",
                      action: (
                        <astryx.Button
                          label="New template"
                          variant="primary"
                          icon={<astryx.Icon icon={Plus} />}
                          href="/admin/script_templates/new"
                        />
                      ),
                    }
              }
            >
              {scriptTemplates.map((scriptTemplate) => {
                const showPath = `/admin/script_templates/${scriptTemplate.id}`;
                const editPath = `/admin/script_templates/${scriptTemplate.id}/edit`;

                return (
                  <PreviewCard
                    key={scriptTemplate.id}
                    title={scriptTemplate.name}
                    description={scriptTemplate.description}
                    onClick={() => navigate(showPath)}
                    footer={
                      <astryx.Text type="supporting" color="secondary">
                        {pluralize(
                          scriptTemplate.scriptTemplateSectionsCount,
                          "section",
                        )}{" "}
                        ·{" "}
                        {pluralize(
                          scriptTemplate.scriptTemplateQuestionsCount,
                          "question",
                        )}
                      </astryx.Text>
                    }
                    menuItems={[
                      { label: "View", onClick: () => navigate(showPath) },
                      { label: "Edit", onClick: () => navigate(editPath) },
                    ]}
                  />
                );
              })}
            </LoadableCardGrid>

            {hasTemplates && (
              <astryx.HStack justify="end">
                <astryx.Pagination
                  page={pagination.page}
                  totalPages={pagination.pages ?? 1}
                  onChange={(page: number) => fetchScriptTemplates(page)}
                  variant="compact"
                />
              </astryx.HStack>
            )}
          </astryx.VStack>
        </ContentContainer>
      </PageContainer>
    </astryx.Theme>
  );
};

export default ScriptTemplates;
